import { useState, useEffect } from 'react';

const ENV_IPS: string[] = (import.meta.env.VITE_ALLOWED_ADMIN_IPS || '')
  .split(',')
  .map((ip: string) => ip.trim())
  .filter((ip: string) => ip.length > 0);

const HARDCODED_IPS: string[] = ['159.196.28.22'];

const ALLOWED_IPS: string[] = [...new Set([...ENV_IPS, ...HARDCODED_IPS])];

interface LoginScreenProps {
  onLoginSuccess: () => void;
}

export default function LoginScreen({ onLoginSuccess }: LoginScreenProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ipStatus, setIpStatus] = useState<'checking' | 'allowed' | 'denied'>('checking');
  const [visitorIp, setVisitorIp] = useState('');

  useEffect(() => {
    const checkIp = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        const ip: string = data.ip;
        setVisitorIp(ip);
        if (ALLOWED_IPS.includes(ip)) {
          setIpStatus('allowed');
        } else {
          setIpStatus('denied');
        }
      } catch {
        // If IP check fails, deny access for security
        setIpStatus('denied');
      }
    };
    checkIp();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch('/api/admin_login.asp', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          username: username,
          password: password,
        }),
      });

      const result = await response.json();

      if (response.ok && result.ok) {
        sessionStorage.setItem('admin_logged_in', 'true');
        onLoginSuccess();
        return;
      } else {
        setError(result.error || 'Invalid username or password');
      }
    } catch {
      const validUsername = import.meta.env.VITE_ADMIN_USERNAME;
      const validPassword = import.meta.env.VITE_ADMIN_PASSWORD;

      if (validUsername && validPassword && username === validUsername && password === validPassword) {
        sessionStorage.setItem('admin_logged_in', 'true');
        onLoginSuccess();
      } else {
        setError('Invalid username or password');
      }
    } finally {
      setLoading(false);
    }
  };

  // IP Checking state
  if (ipStatus === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-200 rounded-2xl mb-4 animate-pulse">
            <i className="ri-shield-check-line text-slate-500 text-3xl"></i>
          </div>
          <p className="text-slate-600 text-sm">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Access Denied state
  if (ipStatus === 'denied') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-2xl mb-6 shadow-lg">
            <i className="ri-shield-cross-line text-red-600 text-4xl"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Access Denied</h1>
          <p className="text-gray-600 mb-6">
            Your IP address is not authorised to access this area.
          </p>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-8 inline-block">
            <p className="text-xs text-red-500 font-mono">
              Your IP: <span className="font-bold text-red-700">{visitorIp || 'Unknown'}</span>
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-start gap-3 text-left">
              <i className="ri-information-line text-slate-400 text-xl mt-0.5 shrink-0"></i>
              <p className="text-sm text-gray-500">
                This admin panel is restricted to authorised IP addresses only. If you believe this is an error, please contact your system administrator.
              </p>
            </div>
          </div>
          <div className="mt-6">
            <a
              href="/"
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors inline-flex items-center gap-2"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Login form (IP allowed)
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl mb-4 shadow-lg">
            <i className="ri-shield-user-line text-white text-3xl"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Access</h1>
          <p className="text-gray-600">Please sign in to continue</p>
        </div>

        {/* IP Allowed Badge */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-xs font-medium px-4 py-2 rounded-full">
            <i className="ri-shield-check-line"></i>
            <span>IP Verified: {visitorIp}</span>
          </div>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleLogin} className="space-y-6">
            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-user-line text-gray-400"></i>
                </div>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                  placeholder="Enter username"
                  required
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-lock-line text-gray-400"></i>
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                  placeholder="Enter password"
                  required
                  autoComplete="current-password"
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <i className="ri-error-warning-line text-red-600 text-xl mt-0.5"></i>
                <div>
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <i className="ri-login-box-line"></i>
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>

          {/* Security Notice */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-start gap-2 text-xs text-gray-500">
              <i className="ri-information-line text-gray-400 mt-0.5"></i>
              <p>
                This is a secure admin area. Unauthorised access is prohibited.
              </p>
            </div>
          </div>
        </div>

        {/* Back to Home Link */}
        <div className="text-center mt-6">
          <a
            href="/"
            className="text-sm text-gray-600 hover:text-gray-900 transition-colors inline-flex items-center gap-2"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </a>
        </div>
      </div>
    </div>
  );
}
